import Styled from './FlightCardPricing.styled';
import {
  Button,
  COLOR_VARIANTS,
  CurrencySymbol,
  Icons,
  Link,
  Tooltip,
  Typography,
  TYPOGRAPHY_VARIANT,
} from '@tajawal/ct-web-design-system';
import { useTranslation } from 'react-i18next';
import { ItineraryModel } from '@tajawal/web-flights-sdk/lib/types/itinerary.types';
import { pushFlightCardDetailsClickedEvent, pushStepFlightSelectedEvent } from '@/utils/avoGtm.utils';
import { getPriceContent, getPriceTagContent, getPriceTagLabel } from '@/utils/content.utils';
import FlightInfoStore from '@tajawal/web-flights-sdk/lib/stores/flight-info.store';
import { useFareFamilyFlowAB } from '@/hooks/useFareFamilyFlowAB';
import _get from 'lodash/get';
import { SeatsRemaining } from '@/app-common/components/SeatsRemaining';
import { isSeatsRemainingAvailable, isSeatsRemainingThreshold } from '@/utils/compute.utils';
import { useMultiVariant } from '@tajawal/web-common/lib';
import { AB_TESTING_FLAGS } from '@/constants/analytics.constants';
import { SeatsRemainingVariant } from '@/types/common.types';
import { useStore } from '@/contexts/StoreContext';
import useStateOfGrouping from '@/hooks/useStateOfGrouping';
import { usePriceUpfrontAB } from '@/hooks/usePriceUpfrontAB';

interface FlightCardPricingProps {
  itinerary: ItineraryModel;
  hasFareFamilies: boolean;
  isInbound: boolean;
  onSelectItinerary: (itinerary: ItineraryModel) => void;
  onCheckPricesClick: () => void;
  onOpenFlightDetails: (itinerary: ItineraryModel) => void;
  index: number;
  testIds: Record<string, string>;
  isFareFamiliesShown: boolean;
  isPaxPage?: boolean;
  $resources?: FlightInfoStore['$resources'];
}

function FlightCardPricing({
  itinerary,
  isInbound,
  hasFareFamilies,
  onSelectItinerary,
  onCheckPricesClick,
  onOpenFlightDetails,
  index,
  testIds,
  $resources,
  isFareFamiliesShown,
  isPaxPage = false,
}: FlightCardPricingProps) {
  const { t } = useTranslation();
  const { displayPrice: priceProps, totalPax, searchRequest, direction } = itinerary;
  const { isRoundTrip } = searchRequest || {};
  const store = useStore();
  const { itineraryGroupMap } = useStateOfGrouping(store, isRoundTrip);
  const { currency } = priceProps || {};
  const { isPriceUpfrontEnabled } = usePriceUpfrontAB();
  const {
    price: total,
    hasDiscount,
    oldTotal,
  } = getPriceContent({ priceProps, isPriceUpfrontActive: isPriceUpfrontEnabled });
  const checkPricesTitle =
    hasFareFamilies && isFareFamiliesShown ? t('FlightCardPricing.HidePrices') : t('FlightCardPricing.CheckPrices');
  const CheckPricesIcon = isFareFamiliesShown ? Icons.ChevronUp : Icons.ChevronDown;

  const { isControlTreatment } = useFareFamilyFlowAB();
  const { activeTreatment, isReady } = useMultiVariant<SeatsRemainingVariant>(AB_TESTING_FLAGS.SEATS_REMAINING);
  const seatsAvailable = _get(itinerary, 'seatsAvailable');

  const isControlUI = isControlTreatment || !isRoundTrip;
  const tag = getPriceTagContent({ hasFareFamilies, searchRequest, isGroup: false, direction, t });

  function handleCheckPricesClick() {
    onCheckPricesClick();
  }

  function handleSelectFlightClick() {
    onSelectItinerary(itinerary);
    if (isRoundTrip) {
      pushStepEvent();
    }
  }

  function handleOpenFlightDetailsClick() {
    onOpenFlightDetails(itinerary);
    pushFlightCardDetailsClickedEvent({
      searchId: itinerary?.id,
      cardPosition: index,
    });

    if (isRoundTrip) {
      pushStepEvent();
    }
  }

  function pushStepEvent() {
    pushStepFlightSelectedEvent({
      itinerary,
      index,
      $resources,
      fareFamilyUpgraded: false,
      hasGroup: itineraryGroupMap.has(itinerary.id),
    });
  }

  const TagLabel = () => {
    if (isPaxPage) {
      return null;
    }
    const label = getPriceTagLabel({
      hasFareFamilies,
      isInbound,
      isRoundTrip,
      t,
    });
    return (
      <Typography
        testId={testIds.startsFrom}
        component="p"
        variant={TYPOGRAPHY_VARIANT.CAPTIONREG}
        color={COLOR_VARIANTS.GREYCHATEAU}
      >
        {isPriceUpfrontEnabled && tag?.label !== null ? tag?.label : label}
      </Typography>
    );
  };

  const shouldShowSeatsRemaining =
    isSeatsRemainingAvailable(activeTreatment, isReady) && isSeatsRemainingThreshold(seatsAvailable, isPaxPage);

  return (
    <Styled.SearchCardPricing>
      <Styled.SearchCardPrice>
        {shouldShowSeatsRemaining && <SeatsRemaining seatsAvailable={seatsAvailable} variant={activeTreatment} />}
        <TagLabel />
        <Styled.Pricing>
          {hasDiscount && (
            <Styled.Discount data-testid={testIds.discount}>
              <CurrencySymbol variant={TYPOGRAPHY_VARIANT.BODY2} currency={currency}>
                {oldTotal}
              </CurrencySymbol>
            </Styled.Discount>
          )}
          {/* TODO: To be replaced with Heading 1 Bold When Available from DS */}
          <Tooltip
            title={
              <Typography align="center" variant={TYPOGRAPHY_VARIANT.BODY2} component="p" color={COLOR_VARIANTS.WHITE}>
                {t('SearchResults.TravellerPricing', { travelers: totalPax, count: totalPax })}
              </Typography>
            }
            placement="top"
          >
            <div>
              <Styled.Price component="h1" variant={TYPOGRAPHY_VARIANT.HEADING3BLD}>
                <CurrencySymbol variant={TYPOGRAPHY_VARIANT.HEADING3BLD} currency={currency}>
                  {total}
                </CurrencySymbol>
              </Styled.Price>
            </div>
          </Tooltip>
        </Styled.Pricing>
      </Styled.SearchCardPrice>
      {!isPaxPage && (
        <Styled.CheckPricesButton>
          {hasFareFamilies && isControlUI && (
            <Button
              testId={testIds.checkPrices}
              fullWidth
              size="small"
              appearance={isFareFamiliesShown ? 'ghost' : 'secondary'}
              endIcon={<CheckPricesIcon />}
              onClick={handleCheckPricesClick}
            >
              {checkPricesTitle}
            </Button>
          )}
          <>
            {(!hasFareFamilies || !isControlUI) && (
              <Button
                testId={testIds.selectFlight}
                fullWidth
                size="small"
                appearance="primary"
                onClick={handleSelectFlightClick}
              >
                <span>{t('FlightCardPricing.SelectFlight')}</span>
              </Button>
            )}

            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link testId={testIds.flightDetails} onClick={handleOpenFlightDetailsClick}>
              <Typography
                color={COLOR_VARIANTS.AQUA100}
                align="center"
                variant={TYPOGRAPHY_VARIANT.HEADING4SEMBLD}
                component="h4"
              >
                {t('FlightDetails.FlightDetails')}
              </Typography>
            </Link>
          </>
        </Styled.CheckPricesButton>
      )}
    </Styled.SearchCardPricing>
  );
}

export default FlightCardPricing;
